'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const AIRCRAFT_TYPES = ['Light Jet', 'Midsize Jet', 'Super Midsize', 'Heavy Jet', 'Ultra Long Range'];

const AIRCRAFT_FLEET = [
  { type: 'Light Jet', example: 'Citation CJ3, Phenom 300', range: 'Up to 2,000 nm', passengers: '6–8 passengers', speed: '450 mph', price: 'From $4,500/hr', icon: '🛩', desc: 'Perfect for short to medium-range trips. Efficient and nimble.' },
  { type: 'Midsize Jet', example: 'Hawker 800, Citation XLS', range: 'Up to 3,000 nm', passengers: '7–9 passengers', speed: '480 mph', price: 'From $6,500/hr', icon: '✈', desc: 'The sweet spot of range, comfort and economy.' },
  { type: 'Super Midsize', example: 'Citation X, Challenger 350', range: 'Up to 4,000 nm', passengers: '8–10 passengers', speed: '530 mph', price: 'From $8,500/hr', icon: '✈', desc: 'Transatlantic capability with stand-up cabin.' },
  { type: 'Heavy Jet', example: 'Gulfstream G450, Falcon 900', range: 'Up to 6,000 nm', passengers: '10–16 passengers', speed: '560 mph', price: 'From $12,000/hr', icon: '✈', desc: 'Intercontinental range with full-service cabin.' },
  { type: 'Ultra Long Range', example: 'Gulfstream G700, Global 7500', range: 'Up to 8,000 nm', passengers: '12–19 passengers', speed: '590 mph', price: 'From $18,000/hr', icon: '✈', desc: 'Non-stop anywhere in the world. The ultimate in private aviation.' },
];

const POPULAR_ROUTES = [
  { from: 'London (EGLL)', to: 'Dubai (OMDB)', aircraft: 'Heavy Jet', duration: '~6.5 hrs', price: 'From $78,000' },
  { from: 'New York (KTEB)', to: 'Miami (KOPF)', aircraft: 'Light Jet', duration: '~2.5 hrs', price: 'From $12,000' },
  { from: 'Geneva (LSGG)', to: 'Nice (LFMN)', aircraft: 'Light Jet', duration: '~45 min', price: 'From $6,500' },
  { from: 'Dubai (OMDB)', to: 'Maldives (VRMM)', aircraft: 'Midsize Jet', duration: '~3.5 hrs', price: 'From $28,000' },
  { from: 'Los Angeles (KVNY)', to: 'Aspen (KASE)', aircraft: 'Midsize Jet', duration: '~2 hrs', price: 'From $18,000' },
  { from: 'Singapore (WSSS)', to: 'Bali (WADD)', aircraft: 'Light Jet', duration: '~2.5 hrs', price: 'From $14,000' },
];

export default function PrivateJetsPage() {
  const router = useRouter();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [departure, setDeparture] = useState('');
  const [passengers, setPassengers] = useState(4);
  const [aircraft, setAircraft] = useState('Heavy Jet');

  const handleQuote = () => {
    const params = new URLSearchParams({
      vertical: 'private_aviation',
      destination: to,
      checkIn: departure,
      travelers: String(passengers),
      budget: aircraft,
    });
    router?.push(`/booking?${params?.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/flights" className="hover:text-[var(--primary)] transition-colors">Flights</Link>
          <Link href="/transfers" className="hover:text-[var(--primary)] transition-colors">Transfers</Link>
          <Link href="/yachts" className="hover:text-[var(--primary)] transition-colors">Yacht Charters</Link>
          <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Private Aviation</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Wheels Up,<br />
            <span className="gold-text-gradient">On Your Schedule.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-10 max-w-xl">
            Instant quotes across the world&apos;s finest private aircraft. Light jets to ultra-long-range. 5,000+ airports worldwide.
          </p>

          {/* Quote Widget */}
          <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.15)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">From (Airport)</label>
                <input type="text" placeholder="City or FBO (e.g. EGLL, Farnborough)" value={from} onChange={(e) => setFrom(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">To (Airport)</label>
                <input type="text" placeholder="City or FBO (e.g. OMDB, Al Maktoum)" value={to} onChange={(e) => setTo(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Departure Date</label>
                <input type="date" value={departure} onChange={(e) => setDeparture(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Passengers</label>
                <select value={passengers} onChange={(e) => setPassengers(Number(e?.target?.value))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 14, 16, 19]?.map((n) => <option key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Aircraft Type</label>
                <select value={aircraft} onChange={(e) => setAircraft(e?.target?.value)} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {AIRCRAFT_TYPES?.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            <button onClick={handleQuote} className="btn-primary w-full py-3 rounded-xl text-sm font-semibold">Request Quote</button>
          </div>
        </div>
      </section>
      {/* Fleet */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Aircraft Categories</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Choose Your <span className="gold-text-gradient">Aircraft</span></h2>
          <div className="space-y-4">
            {AIRCRAFT_FLEET?.map((a) => (
              <div key={a?.type} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all cursor-pointer flex flex-col md:flex-row md:items-center gap-4" onClick={() => setAircraft(a?.type)}>
                <div className="text-3xl flex-shrink-0">{a?.icon}</div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-1">
                    <h3 className="text-base font-bold" style={{ fontFamily: 'var(--font-serif)' }}>{a?.type}</h3>
                    <span className="text-xs text-[var(--muted-foreground)]">{a?.example}</span>
                  </div>
                  <p className="text-xs text-[var(--muted-foreground)] mb-2">{a?.desc}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-[var(--muted-foreground)]">
                    <span>✈ Range: {a?.range}</span>
                    <span>👥 {a?.passengers}</span>
                    <span>⚡ {a?.speed}</span>
                  </div>
                </div>
                <div className="text-sm font-semibold text-[var(--primary)] whitespace-nowrap">{a?.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Popular Routes */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Popular Routes</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Frequently Chartered <span className="gold-text-gradient">Routes</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {POPULAR_ROUTES?.map((r) => (
              <button key={`${r?.from}-${r?.to}`} onClick={() => { setFrom(r?.from); setTo(r?.to); setAircraft(r?.aircraft); }} className="glass-card rounded-xl p-4 text-left hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-[var(--foreground)]">{r?.from}</span>
                  <span className="text-[var(--primary)]">→</span>
                  <span className="text-sm font-medium text-[var(--foreground)]">{r?.to}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[var(--muted-foreground)]">{r?.aircraft} · {r?.duration}</span>
                  <span className="font-semibold text-[var(--primary)]">{r?.price}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/flights" className="hover:text-[var(--primary)] transition-colors">Flights</Link>
            <Link href="/transfers" className="hover:text-[var(--primary)] transition-colors">Transfers</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
